import { useRef, useState, useEffect, useContext, useMemo } from "react";
import SingleDropDown from "./SingleDropDown";
import CheckboxWithLabel from "./CheckboxWithLabel";
import { FormControl } from "@material-ui/core";
import { IncidentsContext } from "../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../Actions/IncidentActions";
import { useStyles } from "../Styles/styles";
import { handleUuid } from "../utils/utils";
import { MutationContext } from "../Contexts/MutationContext";
import { UserContext } from "../Contexts/UserContext";
import { shouldBeLocked } from "../utils/Roles";
import addButton from "../Assets/addButton.png";
import deleteButton from "../Assets/deleteButton.png";

const Charge = ({
  charge,
  numOfCharges,
  chargeIndex,
  incidentIndex,
  incidentId = "",
  incidentOffenses,
  offenseVios,
  arrestIndex,
  arrestId,
  userRoles,
  disabled = undefined,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles]
  );
  const classes = useStyles();
  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const { incidentDispatch } = useContext(IncidentsContext);
  const {
    updateChargeInArrest,
    throttledUpdateChargeInArrest,
    removeChargeFromArrest,
  } = useContext(MutationContext);
  const { user } = useContext(UserContext);
  const { ADD_CHARGE, REMOVE_CHARGE, UPDATE_CHARGE } = INCIDENT_ACTIONS;

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, [inputLabel]);

  const stateToMutation = {
    offenseViolationPrototype: "offenseViolationTypeId",
    nibrsSelected: "nibrsSelected",
  };

  const handleChange = event => {
    let name = event.target.name || null;
    let value = event.target.value;
    const fieldToUpdate = stateToMutation[name];
    if (!name) {
      alert(
        `Please add a name field that matches the state name to the component that was just updated`
      );
      return null;
    } else {
      incidentDispatch({
        type: UPDATE_CHARGE,
        payload: {
          incidentId: incidentId,
          arrestId: arrestId,
          chargeId: charge?.id,
          property: name,
          value: value,
        },
      });
      updateChargeInArrest({
        variables: {
          input: {
            incidentArrestId: arrestId,
            arrestChargeId: charge.id,
            originatorId: user?.id,
            [fieldToUpdate]: value ? value.id : "",
          },
        },
      });
    }
  };

  const handleCheckbox = e => {
    let bool = e.target.checked;
    let name = e.target.value;
    const fieldToUpdate = stateToMutation[name];
    incidentDispatch({
      type: UPDATE_CHARGE,
      payload: {
        incidentId: incidentId,
        arrestId: arrestId,
        chargeId: charge?.id,
        property: name,
        value: bool,
      },
    });
    updateChargeInArrest({
      variables: {
        input: {
          incidentArrestId: arrestId,
          arrestChargeId: charge.id,
          originatorId: user?.id,
          [fieldToUpdate]: bool,
        },
      },
    });
  };

  const handleRemoveCharge = () => {
    incidentDispatch({
      type: REMOVE_CHARGE,
      payload: {
        incidentId: incidentId,
        arrestId: arrestId,
        chargeId: charge?.id,
      },
    });
    removeChargeFromArrest({
      variables: {
        input: { originatorId: user?.id, arrestChargeId: charge.id },
      },
    });
  };

  const handleAddCharge = () => {
    const offlineId = handleUuid();
    const emptyCharge = {
      id: offlineId,
      uuid: offlineId,
      __typename: "ArrestCharges",
      nibrsSelected: false,
      offenseViolationPrototype: null,
    };
    incidentDispatch({
      type: ADD_CHARGE,
      payload: {
        incidentId: incidentId,
        arrestId: arrestId,
        charge: emptyCharge,
      },
    });
    throttledUpdateChargeInArrest({
      variables: {
        input: {
          incidentArrestId: arrestId,
          arrestChargeId: offlineId,
          originatorId: user?.id,
        },
      },
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        border: "1px solid grey",
      }}
    >
      {offenseVios && (
        <SingleDropDown
          handleChange={handleChange}
          stateName={"offenseViolationPrototype"}
          value={charge?.offenseViolationPrototype || {}}
          options={offenseVios}
          title={"Arrest Charge"}
          classStyle={classes.wideForm}
          inputLabel={inputLabel}
          labelWidth={labelWidth}
          name={"fullName"}
          userRoles={userRoles}
          {...props}
        />
      )}
      <CheckboxWithLabel
        formStyle={classes.formControl}
        checked={charge?.sealed || false}
        title={"Sealed"}
        disabled={true}
        userRoles={userRoles}
        {...props}
      />
      <CheckboxWithLabel
        formStyle={classes.formControl}
        checked={charge?.nibrsSelected || false}
        onChange={handleCheckbox}
        stateName={"nibrsSelected"}
        title={"Most Serious Charge"}
        userRoles={userRoles}
        {...props}
      />
      <FormControl
        style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        className={classes.formControl}
      >
        {numOfCharges > 1 && (
          <button
            type="button"
            onClick={!memoizedLocked ? handleRemoveCharge : null}
            disabled={memoizedLocked}
            style={{ background: "none", border: 0, padding: 0 }}
          >
            <img
              data-cy={"delete-charge-button-" + charge?.id}
              src={deleteButton}
              className={classes.pngButton}
              alt="Delete Charge"
            />
          </button>
        )}
        {chargeIndex === numOfCharges - 1 && (
          <button
            type="button"
            onClick={!memoizedLocked ? handleAddCharge : null}
            disabled={memoizedLocked}
            style={{ background: "none", border: 0, padding: 0 }}
          >
            <img
              data-cy={"add-charge-button-" + charge?.id}
              src={addButton}
              className={classes.pngButton}
              alt="Add Charge"
            />
          </button>
        )}
      </FormControl>
    </div>
  );
  // }
};

export default Charge;
